/** Safari / iOS-safe helpers for solution accordion tab videos. */

import { isAppleDevice, isSafari } from './devicePerformance';

const HAVE_METADATA =
  typeof HTMLMediaElement !== 'undefined' ? HTMLMediaElement.HAVE_METADATA : 1;
const HAVE_CURRENT =
  typeof HTMLMediaElement !== 'undefined' ? HTMLMediaElement.HAVE_CURRENT_DATA : 2;
const TAB_LAYOUT_MS = 480;
const READY_EVENTS = ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'];

/** Safari, iOS WebKit, and iPadOS — stricter autoplay / decode rules than Chrome. */
export function isWebKitVideoStrict() {
  if (typeof navigator === 'undefined') return false;
  if (isSafari() || isAppleDevice()) return true;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua);
}

/** Required for muted autoplay on iOS Safari (attribute + property). */
export function applySolutionTabVideoAttrs(el) {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.playsInline = true;
  el.disablePictureInPicture = true;
  el.setAttribute('muted', '');
  el.setAttribute('playsinline', '');
  el.setAttribute('webkit-playsinline', '');
  if (el.hasAttribute('controls')) el.removeAttribute('controls');
}

export function isSolutionTabVideoPlayable(el) {
  return Boolean(el && el.readyState >= HAVE_CURRENT);
}

export function isSolutionTabVideoMetadataReady(el) {
  return Boolean(el && el.readyState >= HAVE_METADATA);
}

/**
 * Assign src after autoplay-safe attrs — Safari rejects playback when order is wrong.
 * Always call before load() / play().
 */
export function assignSolutionTabVideoSource(el, src, { preload = 'auto' } = {}) {
  if (!el || !src) return;
  applySolutionTabVideoAttrs(el);
  el.preload = preload;
  if (el.src !== src) {
    el.src = src;
    el.load();
    return;
  }
  if (preload === 'auto' && el.readyState < HAVE_CURRENT) {
    el.load();
  }
}

/** First decoded frame — loadedmetadata is enough for poster replacement on Safari. */
export function watchSolutionTabVideoReady(el, onReady) {
  if (!el || !onReady) return () => {};

  let done = false;
  const mark = () => {
    if (done || el.readyState < HAVE_METADATA) return;
    done = true;
    READY_EVENTS.forEach((evt) => el.removeEventListener(evt, mark));
    onReady();
  };

  if (el.readyState >= HAVE_METADATA) {
    mark();
    return () => {};
  }

  READY_EVENTS.forEach((evt) => el.addEventListener(evt, mark));
  return () => READY_EVENTS.forEach((evt) => el.removeEventListener(evt, mark));
}

export function playSolutionTabVideo(el) {
  if (!el) return Promise.resolve(false);
  applySolutionTabVideoAttrs(el);
  if (!isSolutionTabVideoPlayable(el)) return Promise.resolve(false);
  return el
    .play()
    .then(() => true)
    .catch(() => false);
}

/**
 * Retry play when active: after load, layout, intersection, and tab width transition.
 * Returns cleanup.
 */
export function watchSolutionTabVideoPlayback(el, isActive, onPlaying) {
  if (!el) return () => {};

  let cancelled = false;
  const cleanups = [];

  const runCleanup = () => {
    cancelled = true;
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
  };

  const firePlaying = () => {
    if (cancelled || !isActive || el.paused) return;
    onPlaying?.();
  };

  const tryPlay = () => {
    if (cancelled || !isActive) return;
    applySolutionTabVideoAttrs(el);
    if (!isSolutionTabVideoPlayable(el)) return;
    void playSolutionTabVideo(el).then((playing) => {
      if (playing) firePlaying();
    });
  };

  const onMediaReady = () => tryPlay();
  READY_EVENTS.forEach((evt) => el.addEventListener(evt, onMediaReady));
  el.addEventListener('playing', firePlaying);
  cleanups.push(() => {
    READY_EVENTS.forEach((evt) => el.removeEventListener(evt, onMediaReady));
    el.removeEventListener('playing', firePlaying);
  });

  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver(
      (entries) => {
        if (cancelled || !isActive) return;
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
          tryPlay();
        }
      },
      { threshold: [0, 0.12, 0.25], rootMargin: '48px 0px' }
    );
    io.observe(el);
    cleanups.push(() => io.disconnect());
  }

  const onVisible = () => {
    if (!document.hidden && isActive) tryPlay();
  };
  document.addEventListener('visibilitychange', onVisible);
  cleanups.push(() => document.removeEventListener('visibilitychange', onVisible));

  tryPlay();
  const raf = requestAnimationFrame(() => {
    requestAnimationFrame(tryPlay);
  });
  cleanups.push(() => cancelAnimationFrame(raf));

  const afterLayout = window.setTimeout(tryPlay, TAB_LAYOUT_MS);
  cleanups.push(() => window.clearTimeout(afterLayout));

  return runCleanup;
}

export { HAVE_CURRENT, HAVE_METADATA, TAB_LAYOUT_MS };
